const getTypeOfFacetValues = facetValues => {
    if (facetValues && typeof facetValues === 'object') {
        const keys = Object.keys(facetValues);
        if (keys.length === 0) {
            return null;
        }

        if (
            keys.every(item => ['true', 'false'].includes(item)) ||
            keys.every(item => item === 'true') ||
            keys.every(item => item === 'false')
        ) {
            return 'bool';
        }

        if (keys.every(key => !isNaN(parseFloat(key)))) {
            return 'range';
        }
    } else {
        return null;
    }

    return 'checkbox';
};

const getFacetOperatorByType = type => {
    if (type === 'bool') {
        return 'and';
    }
    if (type === 'checkbox' || type === 'range') {
        return 'or';
    }
    return null;
};

const getFacetOperatorByValue = (facetName, facetValue, filtersConfig) => {
    let facetValueType;
    if (filtersConfig[facetName] && filtersConfig[facetName].type) {
        facetValueType = filtersConfig[facetName].type;
    } else {
        facetValueType = getTypeOfFacetValues(facetValue);
    }

    return getFacetOperatorByType(facetValueType);
};

export const getOperatorByFacetType = facetType => {
    if (['range', 'bool', 'menu'].includes(facetType)) {
        return 'and';
    }
    if (['checkbox', 'color'].includes(facetType)) {
        return 'or';
    }
    return null;
};

function calcPercent(result) {
    const total = Object.values(result).reduce(
        (aggTotal, value) => aggTotal + value,
        0,
    );

    if (!total) return [];

    const formattedResults = Object.keys(result).reduce((aggreg, key) => {
        aggreg.push({
            category: key,
            percent: ((result[key] * 100) / total).toFixed(2),
            total,
            count: result[key],
        });
        return aggreg;
    }, []);

    formattedResults.sort((a, b) => b.percent - a.percent);

    return formattedResults;
}

function calculateCategoriesPercent(hits = []) {
    const l1Count = { noCat: 0 };
    const l2Count = { noCat: 0 };
    const l3Count = { noCat: 0 };

    hits.forEach(hit => {
        const l1 = hit['categories.l1.id'] || [];
        const l2 = hit['categories.l2.id'] || [];
        const l3 = hit['categories.l3.id'] || [];

        if (l1.length > 0) l1Count[l1[0]] = (l1Count[l1[0]] || 0) + 1;
        else l1Count.noCat += 1;
        if (l2.length > 0) l2Count[l2[0]] = (l2Count[l2[0]] || 0) + 1;
        else l2Count.noCat += 1;
        if (l3.length > 0) l3Count[l3[0]] = (l3Count[l3[0]] || 0) + 1;
        else l3Count.noCat += 1;
    });

    return {
        l1: calcPercent(l1Count),
        l2: calcPercent(l2Count),
        l3: calcPercent(l3Count),
    };
}

export const getCurrentCategoryFromResult = (
    searchResults = {},
    options = { detectionThreshold: 95, detectionLevels: [] },
) => {
    const { detectionThreshold, detectionLevels } = options;
    // if algolia query rule has auto detected category
    const detectedUserData = (searchResults.userData || []).find(
        data => data.categoryDetected,
    );
    if (detectedUserData) {
        const level = detectedUserData.level || 'last';

        // we find categories.last facet
        const categories = searchResults.facets.find(
            facet => facet.name === `categories.${level}`,
        );
        if (categories && searchResults.hits[0][`categories.${level}.id`]) {
            return {
                id: searchResults.hits[0][`categories.${level}.id`][0],
                level,
            };
        }
    }

    const categoriesDetected = calculateCategoriesPercent(searchResults.hits);
    return detectionLevels.reduce((detectedCategory, level) => {
        if (
            detectedCategory === null &&
            categoriesDetected[level] &&
            categoriesDetected[level].length > 0 &&
            categoriesDetected[level][0].percent >= detectionThreshold &&
            categoriesDetected[level][0].category !== 'noCat'
        ) {
            return {
                id: parseInt(categoriesDetected[level][0].category, 10),
                level,
            };
        }
        return detectedCategory;
    }, null);
};

const getAttributeShortName = attributeName =>
    attributeName.replace('catalog_attribute.', '');

const isMandatoryFacet = (attributeName, mandatoryFilters) =>
    mandatoryFilters.includes(attributeName);

const getSpecificFilter = (shortAttributeName, specificFilters = []) => {
    const objectSpecificFilter =
        !!shortAttributeName &&
        !!specificFilters &&
        specificFilters.find(({ id }) => shortAttributeName.includes(id));

    return objectSpecificFilter ? objectSpecificFilter : null; // eslint-disable-line no-unneeded-ternary
};

const bundledFacets = (facets, filterConfig, operator, filtersConfig) => {
    if (!facets.find(facet => facet.attributeName === filterConfig.bundledIn)) {
        return [
            ...facets,
            {
                label: filterConfig.bundledIn,
                ...filtersConfig[filterConfig.bundledIn],
                operator,
                items: [filterConfig],
            },
        ];
    }

    return facets.map(facet => {
        if (facet.id === filterConfig.bundledIn) {
            facet.items.push(filterConfig);
            facet.items.sort(
                (bundledFacetA, bundledFacetB) =>
                    bundledFacetA.order - bundledFacetB.order,
            );
            return facet;
        }
        return facet;
    });
};

export const prepareFacetFilters = ({
    searchFacets,
    specificFilters,
    excludedFilters = [],
    filtersConfig,
    mandatoryFilters,
}) => {
    const inputFacets = Object.keys(searchFacets).filter(
        searchFacet => !excludedFilters.includes(searchFacet),
    );

    return inputFacets.reduce((facets, attributeName, index) => {
        const shortAttributeName = getAttributeShortName(attributeName);
        const specificFilter = getSpecificFilter(
            shortAttributeName,
            specificFilters,
        );
        const operator = getFacetOperatorByValue(
            attributeName,
            searchFacets[attributeName],
            filtersConfig,
        );

        if (
            (isMandatoryFacet(attributeName, mandatoryFilters) ||
                specificFilter) &&
            operator
        ) {
            const filterConfig = filtersConfig[attributeName];

            if (filterConfig && filterConfig.bundledIn) {
                return bundledFacets(
                    facets,
                    filterConfig,
                    operator,
                    filtersConfig,
                );
            }

            let filter;
            if (filterConfig) {
                filter = filterConfig;
            } else if (filtersConfig[shortAttributeName]) {
                filter = filtersConfig[shortAttributeName];
            } else if (specificFilter) {
                filter = specificFilter;
            }
            return [
                ...facets,
                {
                    ...filter,
                    order: filter?.order || index,
                    operator,
                    attributeName,
                },
            ];
        }
        return facets;
    }, []);
};

const getMandatoryFacetFilters = (
    excludedFilters,
    filtersConfig,
    mandatoryFilters,
) =>
    mandatoryFilters.reduce((builtFilters, filterName) => {
        if (filtersConfig[filterName].bundledIn) {
            return bundledFacets(
                builtFilters,
                filtersConfig[filterName],
                getFacetOperatorByType(filtersConfig[filterName].type),
                filtersConfig,
            );
        }

        if (!excludedFilters.includes(filterName)) {
            return [
                ...builtFilters,
                {
                    ...filtersConfig[filterName],
                    operator: getFacetOperatorByType(
                        filtersConfig[filterName].type,
                    ),
                },
            ];
        }

        return builtFilters;
    }, []);

const getEnrichedSpecificFilters = (specificFilters = []) =>
    specificFilters.map(filter => ({
        ...filter,
        attributeName: `catalog_attribute.${filter.id}`,
        operator: getFacetOperatorByType(filter.type),
    }));

export const getFacetFilters = ({
    specificFilters,
    excludedFilters,
    filtersConfig,
    mandatoryFilters,
}) => [
    ...getMandatoryFacetFilters(
        excludedFilters,
        filtersConfig,
        mandatoryFilters,
    ),
    ...getEnrichedSpecificFilters(specificFilters),
];

export default {
    getCurrentCategoryFromResult,
    prepareFacetFilters,
    getOperatorByFacetType,
    getFacetFilters,
};
